/**
 * @file Album related queries.
 */

import gql from 'graphql-tag';

import { ActionableItemFields } from '../fragments/actionable-items';
import {
  AlbumDetails,
  AlbumPrices,
  AlbumListingDetails,
  AlbumMembers,
  PhotoThumbnails,
  AlbumBasicDetails,
  AlbumWatermarkConfig,
  PhotoUrlTypeFields,
  AlbumDetailsForAssign,
  AlbumDeletedPhotosDetails,
  AlbumPrintPackagesPhotoPricingIds,
  AlbumForReview,
  AlbumPrintPackageRecords,
  AlbumFaceHighlightedPhotos,
  AlbumPhotoFaceHighlights,
  AlbumTotalFavoritedPhotos,
  AlbumCustomPrintItems,
  AlbumCustomPrintRecordsForPpdPurchase,
  AlbumMembersRosterIdentities,
  AlbumPhotoGroups as AlbumPhotoGroupsFragment,
  AlbumForStatusItems,
  AlbumRootAlbumFolderBaseDetails,
  AlbumSlideshowDetails,
  AlbumSlideshowPhotosDetails,
  AlbumSlideshowOwnerPhotosDetails,
  AlbumSlideshowLayoutsDetails,
  AlbumPurchasedEventAddonDetails,
  AlbumSlideshowVideoDetails,
  SlideshowCurationPhotoDetails,
  AlbumHierarchyGradeDetails,
  ManualMatchingPhotoDetails,
  AlbumForegroundCountDetails,
} from '../fragments/album';
import {
  AlbumFolderBaseDetails,
  AlbumFolderWatermarkConfig,
  AlbumFolderPricingDetails,
  AlbumFolderEventSettingsFields,
  AlbumFolderPurchasedEventAddonDetails,
  AlbumFolderEventSettingsBasicFields,
} from '../fragments/album-folder';
import { AlbumPhotoGroupBasicDetails } from '../fragments/album-photo-group';
import { UserDetailsWithImages } from '../fragments/account';
import { AlbumFolderTagDetails } from '../fragments/album-folder-tags';
import {
  ContributorAssignmentBaseDetails,
  ContributorAssignmentShareBaseDetails,
  AutoContributorSettingsDetails,
} from '../fragments/contributor-assignments';
import { TransactionItemDetails } from '../fragments/transaction';
import { PurchasedCustomPrintFields } from '../fragments/pro-custom-prints';
import { VideoDetails } from '../fragments/video';
import {
  PhotoFaceHighlightDetails,
  PhotoFavoriteDetails,
  PhotoForegroundDetails,
  PhotoWatermarkThumbnailDetails,
} from '../fragments/photo';
import {
  AccountIdentityName,
  IdentityHasRosterIdentityDetails,
  IdentityRosterIdentityDetails,
} from '../fragments/identity';
import {
  IdentityYearbookPhotoDetails,
  MatchedPhotoYearbookPhotoDetails,
} from '../fragments/yearbook-photos';
import { YearbookExportDetails } from '../fragments/yearbook-exports';

export const AlbumsFromUser = gql`
  query albumsFromUser {
    me {
      id
      albums(access: CREATOR) {
        count
        records {
          ...AlbumListingDetails
        }
      }
      album_memberships {
        records {
          id
          album {
            ...AlbumListingDetails
          }
        }
      }
    }
  }
  ${AlbumListingDetails}
`;

export const AlbumData = gql`
  query albumData($id: RecordID!) {
    album(id: $id) {
      ...AlbumDetails
      ...AlbumPrices
      ...AlbumWatermarkConfig
      ...AlbumPrintPackagesPhotoPricingIds
      ...AlbumCustomPrintItems
      ...AlbumHierarchyGradeDetails
      album_photo_groups {
        total
      }
      root_album_folder {
        ...AlbumFolderBaseDetails
        ...AlbumFolderWatermarkConfig
        ...AlbumFolderPricingDetails
        shoot_type
        tags {
          total
        }
        auto_contributor_settings {
          ...AutoContributorSettingsDetails
        }
        default_matches_only_setting
      }
      subscription_album_folder {
        ...AlbumFolderBaseDetails
      }
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      default_matches_only_setting
      purchased_event_addon_types
    }
  }
  ${AlbumDetails}
  ${AlbumPrices}
  ${AlbumWatermarkConfig}
  ${AlbumPrintPackagesPhotoPricingIds}
  ${AlbumFolderBaseDetails}
  ${AlbumFolderWatermarkConfig}
  ${AlbumCustomPrintItems}
  ${AlbumFolderPricingDetails}
  ${AutoContributorSettingsDetails}
  ${AlbumHierarchyGradeDetails}
`;

/**
 * An attempt to have an album query specifically for the PRO & Owner of the album in question.
 */
export const AlbumDataPRO = gql`
  query albumData($id: RecordID!) {
    album(id: $id) {
      ...AlbumDetails
      ...AlbumPrices
      ...AlbumWatermarkConfig
      ...AlbumPrintPackagesPhotoPricingIds
      ...AlbumCustomPrintItems
      root_album_folder {
        ...AlbumFolderBaseDetails
        ...AlbumFolderWatermarkConfig
        ...AlbumFolderPricingDetails
      }
      subscription_album_folder {
        ...AlbumFolderBaseDetails
      }
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      creator {
        id
      }
      password
      is_private_web_gallery
      invite_only_access
      purchased_event_addon_types
      album_photo_groups {
        total
      }
    }
  }
  ${AlbumDetails}
  ${AlbumPrices}
  ${AlbumWatermarkConfig}
  ${AlbumPrintPackagesPhotoPricingIds}
  ${AlbumFolderBaseDetails}
  ${AlbumFolderWatermarkConfig}
  ${AlbumCustomPrintItems}
  ${AlbumFolderPricingDetails}
  ${AutoContributorSettingsDetails}
`;

export const AlbumDataWithDeletedPhotos = gql`
  query albumDataWithDeletedPhotos($id: RecordID!) {
    album(id: $id) {
      ...AlbumDetails
      ...AlbumPrices
      ...AlbumWatermarkConfig
      ...AlbumDeletedPhotosDetails
      ...AlbumFaceHighlightedPhotos
      ...AlbumTotalFavoritedPhotos
      root_album_folder {
        ...AlbumFolderBaseDetails
        ...AlbumFolderWatermarkConfig
      }
    }
  }
  ${AlbumDetails}
  ${AlbumPrices}
  ${AlbumWatermarkConfig}
  ${AlbumFolderBaseDetails}
  ${AlbumFolderWatermarkConfig}
  ${AlbumDeletedPhotosDetails}
  ${AlbumFaceHighlightedPhotos}
  ${AlbumTotalFavoritedPhotos}
`;

export const AlbumDataForContributors = gql`
  query albumDataForContributors($id: RecordID!) {
    album(id: $id) {
      ...AlbumDetails
      root_album_folder {
        ...AlbumFolderBaseDetails
      }
    }
  }
  ${AlbumDetails}
  ${AlbumFolderBaseDetails}
`;

export const AlbumDataWithParentAndSubscription = gql`
  query albumDataWithParentAndSubscription($id: RecordID!) {
    album(id: $id) {
      ...AlbumDetails
      ...AlbumPrices
      ...AlbumWatermarkConfig
      default_matches_only_setting
      primary_identities_only
      album_folder {
        ...AlbumFolderBaseDetails
      }
      subscription_album_folder {
        ...AlbumFolderBaseDetails
      }
      creator {
        ...UserDetailsWithImages
      }
      album_print_packages {
        total
      }
      custom_prints_meta(custom_print_types: [WALDOGRAM]) {
        onsite_delivery_enabled
        offsite_shipping_enabled
      }
    }
  }
  ${AlbumFolderBaseDetails}
  ${AlbumDetails}
  ${AlbumPrices}
  ${UserDetailsWithImages}
  ${AlbumWatermarkConfig}
`;

export const AlbumMemberData = gql`
  query albumMemberData(
    $id: RecordID!
    $dnpStatusFilter: DnpStatusFilterEnum = ALL
  ) {
    album(id: $id) {
      id
      ...AlbumBasicDetails
      ...AlbumMembers
    }
  }
  ${AlbumBasicDetails}
  ${AlbumMembers}
`;

export const AlbumForSelectedIdentitiesCount = gql`
  query AlbumForSelectedIdentitiesCount($id: RecordID!) {
    album(id: $id) {
      id
      selected_identities(unique_roster_identities: true) {
        total
      }
    }
  }
`;

export const AlbumForIdentityMemberships = gql`
  query albumForIdentityMemberships($id: RecordID!) {
    album(id: $id) {
      id
      ...AlbumBasicDetails
      is_demo
      org_pay_photo_pricing_enabled
      roster_identity_create_enabled
      owner_purchased_joins_enabled
      face_highlighted_photos {
        total
      }
      creator {
        id
        email_address
        organization_name
      }
      album_folder {
        id
        name
      }
      root_album_folder {
        id
        name
        join_type
        individual_albums
        org_pay_photo_pricing_enabled
        roster_identity_create_enabled
        default_album_delivery_mode
        album_delivery_modes {
          photo_finder
          shutterbug
        }
        price {
          value
          currency
        }
        is_demo
        picture_day_enabled
        album_type
        album_subtype
        tag_join_enabled
        tags {
          total
          records {
            id
            ... on IAlbumFolderTag {
              name
              type
            }
          }
        }
        pricing_categories {
          total
          records {
            id
            pricing_category {
              id
              name
            }
          }
        }
        oauth_links(slice: { limit: 999 }) {
          total
          records {
            id
            oauth_account {
              id
              oauth_provider
              user_id
              user_email
              is_active
            }
            oauth_object_id
          }
        }
      }
      unfiltered_identities: selected_identities(
        unique_roster_identities: true
      ) {
        total
      }
      identities_without_yearbook: selected_identities(
        has_yearbook_photo: false
      ) {
        total
      }
    }
  }
  ${AlbumBasicDetails}
`;

export const AlbumMemberDataForRosterIdentities = gql`
  query albumMemberDataForRosterIdentities(
    $id: RecordID!
    $album_folder_id: RecordID!
    $dnpStatusFilter: DnpStatusFilterEnum = ALL
    $limit: Int = 25
    $offset: Int = 0
    $searchQuery: String
    $sortBy: [AlbumSelectedIdentitiesSort]
    $claimedStatusFilter: ClaimedStatusFilterEnum = ALL
    $hasTransactions: Boolean
    $hasMatchedPhotos: Boolean
    $hasActiveReferenceFaces: Boolean
    $uniqueRosterIdentities: Boolean = true
    $includePlicUploadedPhotos: Boolean = false
    $excludePrepaidConfigurations: Boolean = true
    $grade: GradeEnum
  ) {
    album(id: $id) {
      id
      ...AlbumMembersRosterIdentities
    }
  }
  ${AlbumMembersRosterIdentities}
`;

export const AlbumMemberDataForRosterIdentitiesForIdentity = gql`
  query AlbumMemberDataForRosterIdentitiesForIdentity(
    $id: RecordID!
    $identity_ids: [RecordID!]
    $dnpStatusFilter: DnpStatusFilterEnum = ALL
  ) {
    album(id: $id) {
      id
      root_album_folder {
        id
      }
      ...AlbumBasicDetails
      selected_identities(
        identity_ids: $identity_ids
        unique_roster_identities: true
        without_invitees: true
        dnp_status_filter: $dnpStatusFilter
      ) {
        total
        records {
          id
          created_at
          roster_identity {
            id
            selected_identities(album_id: $id, slice: { limit: 40 }) {
              total
              records {
                id
                claimed
                identity {
                  id
                  account {
                    id
                    name
                    phone_number
                    email_address
                    type
                  }
                }
                has_purchased_album_identity
                album_membership {
                  id
                  transaction_items {
                    total
                  }
                }
                matched_photo_notifications {
                  total
                }
              }
            }
          }
        }
      }
    }
  }
  ${AlbumBasicDetails}
`;

export const AlbumMembership = gql`
  query albumMembership($id: RecordID!) {
    albumMembership(id: $id) {
      album {
        id
      }
      purchased_photos(slice: { limit: 999 }) {
        records {
          id
        }
      }
      matched_photos_extended(slice: { limit: 999 }) {
        records {
          photo {
            ...PhotoThumbnails
          }
          compared_face {
            facebox
          }
          identity {
            id
          }
        }
      }
      matched_photos(slice: { limit: 999 }) {
        total
      }
      entitled_matched_photos(slice: { limit: 999 }) {
        total
        records {
          id
        }
      }
    }
  }
  ${PhotoThumbnails}
`;

export const AlbumPhotos = gql`
  query albumPhotos(
    $id: RecordID!
    $ignore_watermark: Boolean!
    $limit: Int
    $offset: Int
    $for_web_gallery: Boolean
    $album_photo_filter: AlbumPhotoFilterEnum = PUBLISHED
    $original_filename: String
    $search_query: String
  ) {
    album(id: $id) {
      ...AlbumBasicDetails
      album_folder {
        id
        name
      }
      photos(
        include_unprocessed: true
        slice: { limit: $limit, offset: $offset }
        for_web_gallery: $for_web_gallery
        album_photo_filter: $album_photo_filter
      ) {
        total
        count
        records {
          id
          original_filename
          thumbnail_urls(ignore_watermark: $ignore_watermark) {
            ...PhotoUrlTypeFields
          }
          watermarked_urls {
            ...PhotoUrlTypeFields
          }
        }
      }
      highlights {
        id
        photo {
          id
        }
      }
      ...AlbumFaceHighlightedPhotos
    }
  }
  ${AlbumBasicDetails}
  ${PhotoUrlTypeFields}
  ${AlbumFaceHighlightedPhotos}
`;

export const AlbumPhotosWithPhotoGroups = gql`
  query albumPhotosWithPhotoGroups(
    $id: RecordID!
    $ignore_watermark: Boolean!
    $limit: Int
    $offset: Int
    $album_photo_filter: AlbumPhotoFilterEnum = PUBLISHED
    $uploader_ids: [RecordID]
    $sort: AlbumPhotoGroupsSort
    $sort_by_shoot_time: Boolean
    $original_filename: String
    $search_query: String
  ) {
    album(id: $id) {
      id
      name
      has_children: has_album_photo_groups
      join_code
      album_type
      album_subtype
      hash_code
      picture_day_enabled
      joins_enabled
      is_video_album
      is_wedding_day_album
      is_memory_book
      is_web_gallery
      is_reference_photos_album
      display_public_photo_attribution
      green_screen_enabled
      hold_matched_photo_deliveries
      has_group_photos
      auto_contributor_settings {
        upload_access
      }
      creator {
        id
      }
      slideshow {
        mode
      }
      cover_photo {
        id
        faces_box
        ...PhotoThumbnails
      }
      album_folder {
        id
        upload_mode
        individual_albums
        individual_photos_upload_type
        team_photos_upload_type
      }
      root_album_folder {
        id
        album_type
        album_subtype
        all_photos_mode
        individual_albums
        search_subscription_credits_joins_enabled
        joins_enabled
        org_pay_photo_pricing_enabled
        picture_day_enabled
        green_screen_enabled
        individual_photos_upload_type
        team_photos_upload_type
        album_photos {
          total
        }
        pricing_categories {
          total
          records {
            id
            pricing_category {
              id
              name
            }
          }
        }
        ...AlbumFolderBaseDetails
        ...AlbumFolderPurchasedEventAddonDetails
      }
      photos(
        include_unprocessed: true
        slice: { limit: $limit, offset: $offset }
      ) {
        total
      }
      ...AlbumDeletedPhotosDetails
      photos_ungrouped: photos(
        include_unprocessed: true
        ungrouped_only: true
        slice: { limit: $limit, offset: $offset }
        album_photo_filter: $album_photo_filter
        sort_by_shoot_time: $sort_by_shoot_time
        uploader_ids: $uploader_ids
        original_filename: $original_filename
        search_query: $search_query
      ) {
        total
        count
        records {
          id
          original_filename
          album_photo_status(album_id: $id)
          is_album_slideshow_photo(album_id: $id)
          album_oauth_link_photo_exists(album_id: $id)
          thumbnail_urls(ignore_watermark: $ignore_watermark) {
            ...PhotoUrlTypeFields
          }
          ...PhotoWatermarkThumbnailDetails
          ...PhotoFavoriteDetails
          is_uploader
          is_owner
          attribution {
            name
            username
            organization_name
            avatar_photo {
              id
              thumbnail_urls {
                ...PhotoUrlTypeFields
              }
            }
            account_id
            photo_attribution_page {
              is_enabled
            }
          }
          description
          share_blocked
          ...PhotoForegroundDetails
        }
      }
      photos_ungrouped_total: photos(
        include_unprocessed: true
        ungrouped_only: true
        slice: { limit: 1 }
        album_photo_filter: ALL
      ) {
        total
      }
      last_uploaded: photos(include_unprocessed: true, slice: { limit: 1 }) {
        records {
          id
          ...PhotoThumbnails
        }
      }
      ...AlbumPhotoGroups
      highlights {
        id
        photo {
          id
        }
      }

      hidden_photos: photos(
        include_unprocessed: true
        ungrouped_only: true
        album_photo_filter: HIDDEN
        uploader_ids: $uploader_ids
      ) {
        total
      }
      reported_photos: photos(
        include_unprocessed: true
        ungrouped_only: true
        album_photo_filter: REPORT_REVIEW
        uploader_ids: $uploader_ids
      ) {
        total
      }
      dnp_photos: photos(
        include_unprocessed: true
        ungrouped_only: true
        album_photo_filter: DNP
        uploader_ids: $uploader_ids
      ) {
        total
      }
      ...AlbumFaceHighlightedPhotos
      ...AlbumForReview
      actionable_items(slice: { limit: 0 }) {
        total
      }
      custom_prints {
        total
      }
      slideshow {
        ...AlbumSlideshowDetails
      }
      unfiltered_videos: videos(processed_only: false) {
        total
      }
      purchased_specialty_prints: purchased_custom_prints(
        slice: { limit: 1 }
        custom_print_subtype: SPECIALTY_PRINT
      ) {
        total
      }
      members_with_matched_photos {
        total
      }
      unmatched_photos: photos(
        include_unprocessed: false
        without_matched_photos_only: true
        slice: { limit: 1 }
        album_photo_filter: ALL
      ) {
        total
      }
      ...AlbumForStatusItems
      ...AlbumTotalFavoritedPhotos
      ...AlbumPurchasedEventAddonDetails
    }
  }
  ${PhotoThumbnails}
  ${PhotoUrlTypeFields}
  ${AlbumDeletedPhotosDetails}
  ${AlbumForReview}
  ${AlbumFaceHighlightedPhotos}
  ${AlbumPhotoGroupsFragment}
  ${AlbumForStatusItems}
  ${PhotoFavoriteDetails}
  ${PhotoWatermarkThumbnailDetails}
  ${AlbumTotalFavoritedPhotos}
  ${AlbumFolderBaseDetails}
  ${AlbumFolderPurchasedEventAddonDetails}
  ${AlbumSlideshowDetails}
  ${AlbumPurchasedEventAddonDetails}
  ${PhotoForegroundDetails}
`;

export const AlbumPhotoGroups = gql`
  query albumPhotoGroups($id: RecordID!) {
    album(id: $id) {
      name
      id
      album_type
      album_subtype
      is_reference_photos_album
      green_screen_enabled
      has_group_photos
      has_children: has_album_photo_groups
      album_photo_groups(slice: { limit: 999 }) {
        total
        records {
          ...AlbumPhotoGroupBasicDetails
        }
      }
      album_folder {
        id
        ...AlbumFolderBaseDetails
      }
      ...AlbumRootAlbumFolderBaseDetails
    }
  }
  ${AlbumPhotoGroupBasicDetails}
  ${AlbumFolderBaseDetails}
  ${AlbumRootAlbumFolderBaseDetails}
`;

export const AlbumHighlights = gql`
  query albumHighlights(
    $id: RecordID!
    $limit: Int = 100
    $offset: Int = 0
    $identityIds: [RecordID] = []
    $favoriteFilter: AlbumPhotosFavoriteFilterEnum = ALL
    $ratingFilter: [Int] = []
  ) {
    album(id: $id) {
      id
      pro_consumer_maybes_enabled
      ...AlbumBasicDetails
      album_folder {
        id
      }
      ...AlbumRootAlbumFolderBaseDetails
      highlights {
        id
        photo {
          ...PhotoThumbnails
          ...PhotoForegroundDetails
        }
      }
      creator {
        id
      }
      face_highlighted_photos(
        slice: { limit: $limit, offset: $offset }
        identity_ids: $identityIds
        favorite_filter: $favoriteFilter
        face_highlight_ratings: $ratingFilter
      ) {
        total
        records {
          id
          ...AlbumPhotoFaceHighlights
        }
      }
    }
  }
  ${AlbumBasicDetails}
  ${PhotoThumbnails}
  ${PhotoForegroundDetails}
  ${AlbumPhotoFaceHighlights}
  ${AlbumRootAlbumFolderBaseDetails}
`;

export const AlbumHighlightsForWebGallery = gql`
  query albumHighlightsForWebGallery($id: RecordID!) {
    album(id: $id) {
      id
      ...AlbumBasicDetails
      album_folder {
        id
      }
      root_album_folder {
        id
      }
      highlights {
        id
        photo {
          ...PhotoThumbnails
          ...PhotoFavoriteDetails
          is_owner
        }
      }
      creator {
        id
      }
    }
  }
  ${AlbumBasicDetails}
  ${PhotoThumbnails}
  ${PhotoFavoriteDetails}
`;

// TODO combine this with some other query?
// Refactor AlbumPhotos / AlbumData
export const AlbumPhotosWithFaceboxes = gql`
  query albumPhotosWithFaceboxes($id: RecordID!) {
    album(id: $id) {
      id
      photos(include_unprocessed: true) {
        total
        records {
          ...PhotoThumbnails
          faces {
            records {
              facebox
            }
          }
        }
      }
    }
  }
  ${PhotoThumbnails}
`;

// TODO refactor slice/limit/offset when adding pagination
export const IdentityPhotosFromMembership = gql`
  query identityPhotosFromMembership(
    $id: RecordID!
    $album_id: RecordID!
    $identity_ids: [RecordID!]
    $plic_uploaded_only: Boolean
  ) {
    albumMembership(id: $id) {
      id
      matched_photos_extended(
        slice: { limit: 999 }
        plic_uploaded_only: $plic_uploaded_only
      ) {
        total
        records {
          ...PhotoThumbnails
          ...PhotoFavoriteDetails
          album_photo_status(album_id: $album_id)
          album_oauth_link_photo_exists(album_id: $album_id)
          share_blocked
          is_owner
        }
      }
    }
  }
  ${PhotoThumbnails}
  ${PhotoFavoriteDetails}
`;

export const IdentityAlbumPhotos = gql`
  query identityPhotosFromMembership(
    $id: RecordID!
    $album_id: RecordID!
    $limit: Int = 100
    $offset: Int = 0
    $plic_uploaded_only: Boolean
    $matched_photos_sort: MatchedPhotosSortEnum
    $account_id: RecordID
  ) {
    identity(id: $id) {
      id
      ...IdentityRosterIdentityDetails
      matched_photos_extended(
        album_ids: [$album_id]
        slice: { limit: $limit, offset: $offset }
        plic_uploaded_only: $plic_uploaded_only
        matched_photos_sort: $matched_photos_sort
      ) {
        total
        records {
          id
          is_entitled_matched_photo(account_id: $account_id)
          identity {
            id
          }
          photo {
            ...PhotoThumbnails
            ...PhotoFavoriteDetails
            ...PhotoForegroundDetails
            is_owner
            original_filename
            album_photo_status(album_id: $album_id)
            album_oauth_link_photo_exists(album_id: $album_id)
            share_blocked
            faces(
              slice: { limit: 1 }
              exclude_machine_labeled_pub_boxes: true
              exclude_human_labeled_pub_boxes: true
            ) {
              total
              records {
                facebox
              }
            }
            ...PhotoFaceHighlightDetails
          }
          ...MatchedPhotoYearbookPhotoDetails
        }
      }
    }
    album(id: $album_id) {
      id
      owner_purchased_joins_enabled
    }
  }
  ${PhotoThumbnails}
  ${PhotoFavoriteDetails}
  ${PhotoForegroundDetails}
  ${PhotoFaceHighlightDetails}
  ${IdentityRosterIdentityDetails}
  ${MatchedPhotoYearbookPhotoDetails}
`;

export const Photo = gql`
  query photo($id: RecordID!) {
    photo(id: $id) {
      ...PhotoThumbnails
    }
  }
  ${PhotoThumbnails}
`;

export const AlbumByJoinCode = gql`
  query albumByJoinCode($joinCode: String!) {
    albumByJoinCode(joinCode: $joinCode) {
      ...AlbumDetails
    }
  }
  ${AlbumDetails}
`;

export const Albums = gql`
  query albums($sort: AlbumSort, $slice: SlicePage, $join_code: String) {
    albums(sort: $sort, slice: $slice, join_code: $join_code) {
      total
      records {
        id
        name
        join_code
        hash_code
        album_type
        last_uploaded_at
        members {
          total
        }
        selected_identities {
          total
        }
        photos {
          total
        }
      }
    }
  }
`;

export const AlbumDeletedPhotos = gql`
  query albumDeletedPhotos($id: RecordID!, $limit: Int, $offset: Int) {
    album(id: $id) {
      ...AlbumBasicDetails
      album_folder {
        id
        name
      }
      deleted_photos(slice: { limit: $limit, offset: $offset }) {
        total
        records {
          id
          photo {
            id
            thumbnail_urls(ignore_watermark: true) {
              ...PhotoUrlTypeFields
            }
            watermarked_urls {
              ...PhotoUrlTypeFields
            }
          }
        }
      }
    }
  }
  ${AlbumBasicDetails}
  ${PhotoUrlTypeFields}
`;

export const IndividualAlbums = gql`
  query individualAlbums {
    me {
      id
      albums(access: CREATOR, individual_albums_only: true) {
        count
        records {
          ...AlbumDetails
          ...AlbumDetailsForAssign
          ...AlbumDeletedPhotosDetails
        }
      }
    }
  }
  ${AlbumDeletedPhotosDetails}
  ${AlbumDetails}
  ${AlbumDetailsForAssign}
`;

export const IndividualAlbumsForListing = gql`
  query individualAlbumsForListing {
    me {
      id
      albums(access: CREATOR, without_album_folder: true) {
        count
        records {
          ...AlbumDetails
          ...AlbumDeletedPhotosDetails
          ...AlbumDetailsForAssign
          cover_photo {
            id
            faces_box
            thumbnail_urls(ignore_watermark: true) {
              ...PhotoUrlTypeFields
            }
          }
          members {
            total
          }
        }
      }
    }
  }
  ${AlbumDeletedPhotosDetails}
  ${AlbumDetails}
  ${AlbumDetailsForAssign}
  ${PhotoUrlTypeFields}
`;

export const AlbumForSelectAll = gql`
  query albumForSelectAll(
    $id: RecordID!
    $album_photo_filter: AlbumPhotoFilterEnum = PUBLISHED
  ) {
    album(id: $id) {
      id
      photos_ungrouped: photos(
        include_unprocessed: true
        ungrouped_only: true
        slice: { limit: 9999, offset: 0 }
        album_photo_filter: $album_photo_filter
      ) {
        total
        count
        records {
          id
        }
      }
    }
  }
`;

export const AlbumForPpdPurchase = gql`
  query albumForPpdPurchase($id: RecordID!) {
    album(id: $id) {
      ...AlbumDetails
      ...AlbumPrices
      ...AlbumWatermarkConfig
      ...AlbumPrintPackageRecords
      ...AlbumCustomPrintRecordsForPpdPurchase
      subscription_album_folder {
        ...AlbumFolderBaseDetails
      }
      root_album_folder {
        ...AlbumFolderBaseDetails
      }
      creator {
        ...UserDetailsWithImages
      }
    }
  }
  ${AlbumDetails}
  ${AlbumPrices}
  ${AlbumWatermarkConfig}
  ${AlbumPrintPackageRecords}
  ${AlbumCustomPrintRecordsForPpdPurchase}
  ${AlbumFolderBaseDetails}
  ${UserDetailsWithImages}
`;

export const AlbumMembershipWithPhotoTagPrice = gql`
  query albumMembershipWithPhotoTagPrice($id: RecordID!, $album_id: RecordID!) {
    albumMembership(id: $id) {
      album {
        id
      }
      purchased_photos(slice: { limit: 999 }) {
        records {
          id
        }
      }
      matched_photos_extended(slice: { limit: 999 }) {
        records {
          photo {
            ...PhotoThumbnails
            price_album_folder_tag(album_id: $album_id) {
              ...AlbumFolderTagDetails
            }
          }
          compared_face {
            facebox
          }
          identity {
            id
          }
        }
      }
      matched_photos(slice: { limit: 999 }) {
        total
      }
      entitled_matched_photos(slice: { limit: 999 }) {
        total
        records {
          id
        }
      }
    }
  }
  ${PhotoThumbnails}
  ${AlbumFolderTagDetails}
`;

export const AlbumContributorAssignments = gql`
  query albumContributorAssignments($id: RecordID!) {
    album(id: $id) {
      id
      name
      album_type
      album_subtype
      is_web_gallery
      root_album_folder {
        id
        is_web_gallery
        individual_albums
      }
      purchased_event_addon_types
      contributor_assignments(slice: { limit: 999 }, include_inherited: true) {
        records {
          ...ContributorAssignmentBaseDetails
        }
      }
      contributor_assignment_shares(
        slice: { limit: 999 }
        include_inherited: true
        pending_only: true
        without_account_uuid: false
      ) {
        records {
          ...ContributorAssignmentShareBaseDetails
        }
      }
    }
  }
  ${ContributorAssignmentBaseDetails}
  ${ContributorAssignmentShareBaseDetails}
`;

export const PhotoForHighlights = gql`
  query photoForHighlights($id: RecordID!, $albumId: RecordID!) {
    photo(id: $id) {
      id
      ...PhotoThumbnails
      faces(
        slice: { limit: 999 }
        exclude_machine_labeled_pub_boxes: true
        exclude_human_labeled_pub_boxes: true
      ) {
        records {
          id
          facebox
          matched_photos(slice: { limit: 1 }, album_ids: [$albumId]) {
            records {
              id
              identity {
                id
                name
                current_photo {
                  ...PhotoThumbnails
                }
              }
            }
          }
        }
      }
      face_highlights(album_id: $albumId) {
        records {
          id
          rating
          face {
            id
            facebox
            matched_photos(slice: { limit: 1 }, album_ids: [$albumId]) {
              records {
                id
                identity {
                  id
                  name
                  current_photo {
                    ...PhotoThumbnails
                  }
                }
              }
            }
          }
          album {
            id
          }
        }
      }
    }
  }
  ${PhotoThumbnails}
`;

export const PhotoForCropping = gql`
  query photoForCropping($id: RecordID!) {
    photo(id: $id) {
      id
      ...PhotoThumbnails
      faces(
        slice: { limit: 1 }
        exclude_machine_labeled_pub_boxes: true
        exclude_human_labeled_pub_boxes: true
      ) {
        total
        records {
          id
          facebox
        }
      }
    }
  }
  ${PhotoThumbnails}
`;

export const AlbumFavorites = gql`
  query albumFavorites(
    $id: RecordID!
    $limit: Int = 100
    $offset: Int = 0
    $sort_by: [AlbumFavoritedPhotosSortInput] = []
    $favorited_by: FavoritedPhotosFilterEnum = ALL
  ) {
    album(id: $id) {
      id
      highlights {
        id
        photo {
          id
        }
      }
      favorited_photos(
        slice: { limit: $limit, offset: $offset }
        sort_by: $sort_by
        favorited_by: $favorited_by
      ) {
        total
        records {
          id
          ...AlbumPhotoFaceHighlights
        }
      }
      ...AlbumRootAlbumFolderBaseDetails
    }
  }
  ${AlbumPhotoFaceHighlights}
  ${AlbumRootAlbumFolderBaseDetails}
`;

export const AlbumForRevenue = gql`
  query albumForRevenue(
    $id: RecordID!
    $limit: Int = 25
    $offset: Int = 0
    $accountsFilter: AlbumAccountsFilter = WITH_TRANSACTION_ITEMS
  ) {
    album(id: $id) {
      ...AlbumDetails
      accounts(
        slice: { limit: $limit, offset: $offset }
        accounts_filter: $accountsFilter
      ) {
        total
        records {
          id
          account {
            id
            name
            phone_number
          }
          identities(slice: { limit: 1 }) {
            records {
              id
              name
              last_name
            }
          }
          transaction_items(slice: { limit: 999 }) {
            records {
              ...TransactionItemDetails
            }
          }
        }
      }
      transaction_items_revenue {
        gross {
          value
          currency
        }
        print_cost {
          value
          currency
        }
        shipping_cost {
          value
          currency
        }
        stripe_fee {
          value
          currency
        }
        waldo_fee {
          value
          currency
        }
        refunds {
          value
          currency
        }
        tax {
          value
          currency
        }
        vendor_tax {
          value
          currency
        }
        net {
          value
          currency
        }
        transactions_count
        avg_net_per_transaction
      }
    }
  }
  ${AlbumDetails}
  ${TransactionItemDetails}
`;

export const AlbumPhotosForSearch = gql`
  query AlbumPhotosForSearch(
    $id: RecordID!
    $limit: Int
    $offset: Int
    $tagIds: [RecordID]
    $filterMode: CollectionFilterModeEnum = ANY
    $original_filename: String
    $search_query: String
  ) {
    album(id: $id) {
      id
      name
      hash_code
      ...AlbumBasicDetails
      ...AlbumRootAlbumFolderBaseDetails
      photos(
        slice: { limit: $limit, offset: $offset }
        album_folder_tag_ids: $tagIds
        album_folder_tag_ids_mode: $filterMode
        original_filename: $original_filename
        search_query: $search_query
      ) {
        total
        records {
          id
          is_album_slideshow_photo(album_id: $id)
          ...PhotoThumbnails
          ...PhotoForegroundDetails
          share_blocked
        }
      }
    }
  }
  ${PhotoThumbnails}
  ${AlbumBasicDetails}
  ${PhotoForegroundDetails}
  ${AlbumRootAlbumFolderBaseDetails}
`;

export const AlbumPhotosForSearchForContributors = gql`
  query AlbumPhotosForSearchForContributors(
    $id: RecordID!
    $limit: Int
    $offset: Int
    $tagIds: [RecordID]
    $filterMode: CollectionFilterModeEnum = ANY
    $original_filename: String
    $search_query: String
  ) {
    album(id: $id) {
      id
      name
      ...AlbumBasicDetails
      root_album_folder {
        id
      }
      photos(
        slice: { limit: $limit, offset: $offset }
        album_folder_tag_ids: $tagIds
        album_folder_tag_ids_mode: $filterMode
        original_filename: $original_filename
        search_query: $search_query
      ) {
        total
        records {
          id
          ...PhotoThumbnails
          share_blocked
        }
      }
    }
  }
  ${PhotoThumbnails}
  ${AlbumBasicDetails}
`;

export const AlbumForCustomPrintsOrders = gql`
  query albumForCustomPrintsOrders(
    $id: RecordID!
    $limit: Int
    $offset: Int
    $orderStatus: [CustomPrintOrderStatusEnum]
  ) {
    album(id: $id) {
      ...AlbumBasicDetails
      picture_day_enabled
      purchased_custom_prints(
        slice: { limit: $limit, offset: $offset }
        order_statuses: $orderStatus
        excluded_delivery_types: [EXTERNAL_SHIPPING]
      ) {
        total
        records {
          ...PurchasedCustomPrintFields
        }
      }

      purchased_specialty_prints: purchased_custom_prints(
        slice: { limit: 1 }
        custom_print_subtype: SPECIALTY_PRINT
      ) {
        total
      }

      customer_input: purchased_custom_prints(
        slice: { limit: 1 }
        order_statuses: [CUSTOMER_INPUT]
        excluded_delivery_types: [EXTERNAL_SHIPPING]
      ) {
        total
      }

      pending: purchased_custom_prints(
        slice: { limit: 1 }
        order_statuses: [PENDING]
        excluded_delivery_types: [EXTERNAL_SHIPPING]
      ) {
        total
      }

      shipped: purchased_custom_prints(
        slice: { limit: 1 }
        order_statuses: [SHIPPED]
        excluded_delivery_types: [EXTERNAL_SHIPPING]
      ) {
        total
      }

      canceled: purchased_custom_prints(
        slice: { limit: 1 }
        order_statuses: [CANCELED]
        excluded_delivery_types: [EXTERNAL_SHIPPING]
      ) {
        total
      }
    }
  }
  ${AlbumBasicDetails}
  ${PurchasedCustomPrintFields}
`;

export const AlbumWithCoverPhotos = gql`
  query albumWithCoverPhotos(
    $albumId: RecordID!
    $limit: Int = 20
    $offset: Int = 0
  ) {
    album(id: $albumId) {
      id
      custom_cover_photos {
        records {
          id
          display_order
          photo {
            id
            thumbnail_urls {
              ...PhotoUrlTypeFields
            }
          }
        }
      }
      photos(
        include_unprocessed: true
        slice: { limit: $limit, offset: $offset }
      ) {
        total
        count
        records {
          id
          thumbnail_urls {
            ...PhotoUrlTypeFields
          }
        }
      }
    }
  }
  ${PhotoUrlTypeFields}
`;

export const AlbumPhotosWithPhotoGroupsForContributors = gql`
  query albumPhotosWithPhotoGroupsForContributors(
    $id: RecordID!
    $ignore_watermark: Boolean!
    $limit: Int
    $offset: Int
    $album_photo_filter: AlbumPhotoFilterEnum = ALL
  ) {
    album(id: $id) {
      id
      name
      has_children: has_album_photo_groups
      join_code
      hash_code
      album_type
      album_subtype
      is_video_album
      is_wedding_day_album
      is_memory_book
      display_public_photo_attribution
      creator {
        id
      }
      album_folder {
        id
        upload_mode
      }
      ...AlbumRootAlbumFolderBaseDetails
      photos(slice: { limit: $limit, offset: $offset }) {
        total
      }
      photos_ungrouped: photos(
        ungrouped_only: true
        slice: { limit: $limit, offset: $offset }
        album_photo_filter: $album_photo_filter
      ) {
        total
        count
        records {
          id
          original_filename
          album_photo_status(album_id: $id)
          thumbnail_urls(ignore_watermark: $ignore_watermark) {
            ...PhotoUrlTypeFields
          }
          is_uploader
          description
          watermarked_urls {
            ...PhotoUrlTypeFields
          }
          is_owner
          ...PhotoFavoriteDetails
          attribution {
            name
            username
            organization_name
            avatar_photo {
              id
              thumbnail_urls {
                ...PhotoUrlTypeFields
              }
            }
            account_id
            photo_attribution_page {
              is_enabled
            }
          }
          share_blocked
        }
      }
      last_uploaded: photos(include_unprocessed: true, slice: { limit: 1 }) {
        records {
          id
          ...PhotoThumbnails
        }
      }
      album_photo_groups(slice: { limit: 999 }) {
        total
        records {
          id
          name
          cover_photos {
            records {
              faces_box
              ...PhotoThumbnails
            }
          }
          photos(
            include_unprocessed: true
            slice: { limit: $limit, offset: $offset }
            album_photo_filter: PUBLISHED
          ) {
            total
          }
          last_uploaded: photos(
            include_unprocessed: true
            slice: { limit: 1 }
          ) {
            records {
              id
              ...PhotoThumbnails
            }
          }
        }
      }
      highlights {
        id
        photo {
          id
        }
      }
      slideshow {
        ...AlbumSlideshowDetails
      }
      purchased_event_addon_types
      unmatched_photos: photos(
        include_unprocessed: false
        without_matched_photos_only: true
        slice: { limit: 1 }
        album_photo_filter: ALL
      ) {
        total
      }
    }
  }
  ${PhotoThumbnails}
  ${PhotoUrlTypeFields}
  ${PhotoFavoriteDetails}
  ${AlbumRootAlbumFolderBaseDetails}
  ${AlbumSlideshowDetails}
`;

export const AlbumActionableItems = gql`
  query albumActionableItems(
    $id: RecordID!
    $limit: Int = 20
    $offset: Int = 0
  ) {
    album(id: $id) {
      id
      actionable_items(slice: { limit: $limit, offset: $offset }) {
        total
        records {
          ...ActionableItemFields
        }
      }
      name
    }
  }
  ${ActionableItemFields}
`;

export const AlbumVideosPro = gql`
  query albumVideos(
    $id: RecordID!
    $limit: Int = 25
    $offset: Int = 0
    $status: VideoStatusEnum = PUBLISHED
    $uploader_ids: [RecordID] = []
    $sort_by_type: Boolean
    $include_user_uploaded_videos: Boolean = false
    $user_uploaded_videos: RecordID
  ) {
    album(id: $id) {
      id
      name
      album_type
      album_subtype
      is_video_album
      is_wedding_day_album
      is_memory_book
      creator {
        id
      }
      album_folder {
        id
        auto_contributor_share_url
        individual_albums
      }
      user_uploaded_videos: videos(
        processed_only: false
        uploader_ids: [$user_uploaded_videos]
      ) @include(if: $include_user_uploaded_videos) {
        total
        records {
          id
          type
        }
      }
      unfiltered_videos: videos(processed_only: false) {
        total
      }
      videos(
        processed_only: false
        slice: { limit: $limit, offset: $offset }
        status: $status
        uploader_ids: $uploader_ids
        sort_by_type: $sort_by_type
      ) {
        total
        records {
          id
          ...VideoDetails
        }
      }
      purchased_event_addon_types
      auto_contributor_share_url
      auto_contributor_settings {
        upload_access
      }
    }
  }
  ${VideoDetails}
`;

export const AlbumHighlight = gql`
  query albumHighlight($id: RecordID!) {
    albumHighlight(id: $id) {
      id
      photo {
        id
        ...PhotoThumbnails
      }
      album {
        id
        ...AlbumBasicDetails
        ...AlbumRootAlbumFolderBaseDetails
      }
    }
  }
  ${AlbumBasicDetails}
  ${PhotoThumbnails}
  ${AlbumRootAlbumFolderBaseDetails}
`;

export const AlbumPhotosReview = gql`
  query AlbumPhotosReview(
    $id: RecordID!
    $limit: Int
    $offset: Int
    $uploader_ids: [RecordID]
  ) {
    album(id: $id) {
      id
      ...AlbumBasicDetails
      ...AlbumRootAlbumFolderBaseDetails
      photos(
        slice: { limit: $limit, offset: $offset }
        album_photo_filter: REVIEWING
        uploader_ids: $uploader_ids
      ) {
        total
        records {
          id
          ...PhotoThumbnails
          ...PhotoForegroundDetails
        }
      }
    }
  }
  ${AlbumBasicDetails}
  ${PhotoThumbnails}
  ${PhotoForegroundDetails}
  ${AlbumRootAlbumFolderBaseDetails}
`;

export const AlbumSlideshow = gql`
  query AlbumSlideshow($id: RecordID!, $offset: Int = 0, $limit: Int = 10) {
    album(id: $id) {
      id
      name
      hash_code
      album_type
      album_subtype
      purchased_event_addon_types
      consumer_photos_sort_direction
      auto_contributor_settings {
        view_access
        upload_access
      }
      auto_contributor_share_url
      creator {
        id
        organization_name
        username
        name
        logo_image {
          id
          url
        }
      }
      root_album_folder {
        id
        account {
          id
          organization_name
          username
          name
        }
        album_subtype
        event_settings {
          ...AlbumFolderEventSettingsBasicFields
        }
      }
      slideshow {
        ...AlbumSlideshowDetails
        ...AlbumSlideshowPhotosDetails
        ...AlbumSlideshowOwnerPhotosDetails
        ...AlbumSlideshowLayoutsDetails
      }
      photos {
        total
      }
      unmatched_photos: photos(
        include_unprocessed: false
        without_matched_photos_only: true
        slice: { limit: 1 }
        album_photo_filter: ALL
      ) {
        total
      }
      last_uploaded_at
    }
  }
  ${AlbumFolderEventSettingsBasicFields}
  ${AlbumSlideshowDetails}
  ${AlbumSlideshowPhotosDetails}
  ${AlbumSlideshowOwnerPhotosDetails}
  ${AlbumSlideshowLayoutsDetails}
`;
export const AlbumSlideshowForContributors = gql`
  query AlbumSlideshowForContributors(
    $id: RecordID!
    $offset: Int = 0
    $limit: Int = 10
  ) {
    album(id: $id) {
      id
      name
      hash_code
      album_type
      album_subtype
      purchased_event_addon_types
      consumer_photos_sort_direction
      auto_contributor_settings {
        upload_access
      }
      auto_contributor_share_url
      creator {
        id
        organization_name
        username
        name
        logo_image {
          id
          url
        }
      }
      root_album_folder {
        id
        account {
          id
          organization_name
          username
          name
        }
        album_subtype
        event_settings {
          ...AlbumFolderEventSettingsBasicFields
        }
      }
      slideshow {
        ...AlbumSlideshowDetails
        ...AlbumSlideshowPhotosDetails
        ...AlbumSlideshowLayoutsDetails
      }
      last_uploaded_at
    }
  }
  ${AlbumFolderEventSettingsBasicFields}
  ${AlbumSlideshowDetails}
  ${AlbumSlideshowPhotosDetails}
  ${AlbumSlideshowLayoutsDetails}
`;

export const AlbumSlideshowForUnprocessedPhotos = gql`
  query AlbumSlideshowForUnprocessedPhotos(
    $id: RecordID!
    $include_slideshow_photo_data: Boolean = false
  ) {
    album(id: $id) {
      id
      slideshow @include(if: $include_slideshow_photo_data) {
        photos {
          total
        }
        unprocessed_photos: photos(unprocessed_only: true) {
          total
        }
      }
      last_uploaded_at
    }
  }
`;

export const AlbumSlideshowForPendingLayouts = gql`
  query AlbumSlideshowForPendingLayouts($id: RecordID!) {
    album(id: $id) {
      id
      slideshow {
        has_pending_layouts_create
        layouts {
          total
        }
      }
    }
  }
`;

export const AlbumForVideoGuestBook = gql`
  query albumForVideoGuestBook(
    $id: RecordID!
    $user_uploaded_videos: RecordID
    $include_user_uploaded_videos: Boolean = false
  ) {
    album(id: $id) {
      id
      is_video_album
      is_wedding_day_album
      is_memory_book
      user_uploaded_videos: videos(
        processed_only: false
        uploader_ids: [$user_uploaded_videos]
      ) @include(if: $include_user_uploaded_videos) {
        total
      }
      videos(processed_only: false, slice: { limit: 1 }, sort_by_type: true) {
        total
        records {
          id
          ...VideoDetails
        }
      }
      album_folder {
        id
        name
        account {
          id
        }
      }
    }
  }
  ${VideoDetails}
`;

export const AlbumWithGreetingVideo = gql`
  query albumWithGreetingVideo($id: RecordID!) {
    album(id: $id) {
      id
      name
      album_type
      album_subtype
      is_video_album
      is_memory_book
      is_wedding_day_album
      creator {
        id
        name
      }
      root_album_folder {
        id
        name
        album_type
        album_subtype
        account {
          id
          name
          organization_name
        }
        event_settings {
          ...AlbumFolderEventSettingsFields
        }
      }
      greeting_video: videos(
        slice: { limit: 1 }
        types: [GREETING]
        processed_only: false
      ) {
        total
        records {
          ...VideoDetails
        }
      }
      purchased_event_addon_types
      slideshow {
        mode
        default_greeting_video {
          ...VideoDetails
        }
        dismissed_default_greeting_video
      }
    }
  }
  ${AlbumFolderEventSettingsFields}
  ${VideoDetails}
`;

export const AlbumWithEventSettings = gql`
  query albumWithEventSettings($id: RecordID!) {
    album(id: $id) {
      id
      album_type
      album_subtype
      auto_contributor_share_url
      purchased_event_addon_types
      creator {
        id
      }
      slideshow {
        status
      }
      root_album_folder {
        id
        name
        album_type
        album_subtype
        account {
          id
          name
          organization_name
        }
        event_settings {
          ...AlbumFolderEventSettingsFields
        }
      }
      purchased_event_addon_types
    }
  }
  ${AlbumFolderEventSettingsFields}
`;

export const AlbumWithPurchasedEventAddonTypes = gql`
  query albumWithPurchasedEventAddonTypes($id: RecordID!) {
    album(id: $id) {
      id
      album_type
      album_subtype
      purchased_event_addon_types
    }
  }
`;

export const AlbumVideoSlideshow = gql`
  query AlbumVideoSlideshow(
    $id: RecordID!
    $offset: Int = 0
    $limit: Int = 10
  ) {
    album(id: $id) {
      id
      name
      album_type
      album_subtype
      purchased_event_addon_types
      creator {
        id
        organization_name
        username
        name
        logo_image {
          id
          url
        }
      }
      videos(processed_only: false) {
        total
      }
      processed_videos: videos(processed_only: true) {
        total
      }
      slideshow {
        ...AlbumSlideshowVideoDetails
      }
    }
  }
  ${AlbumSlideshowVideoDetails}
`;

export const AlbumSlideshowForModeCheck = gql`
  query AlbumSlideshowForModeCheck($id: RecordID!) {
    album(id: $id) {
      id
      name
      privacy
      album_subtype
      creator {
        id
      }
      root_album_folder {
        id
        account {
          id
          name
          organization_name
        }
        event_settings {
          ...AlbumFolderEventSettingsFields
        }
      }
      slideshow {
        mode
        status
        photos(slice: { limit: 1 }) {
          total
          records {
            photo {
              id
              ...PhotoThumbnails
            }
          }
        }
        intro_layout {
          account_card {
            id
            front_preview_file_url
          }
        }
        selected_photos {
          total
        }
        layouts(slice: { limit: 1 }) {
          total
          records {
            account_card {
              id
              front_preview_file_url
            }
          }
        }
        videos {
          total
        }
      }
    }
  }
  ${PhotoThumbnails}
  ${AlbumFolderEventSettingsFields}
`;

export const AlbumSlideshowForLastRegeneratedAtCheck = gql`
  query AlbumSlideshowForLastRegeneratedAtCheck($id: RecordID!) {
    album(id: $id) {
      id
      slideshow {
        last_regenerated_at
      }
    }
  }
`;

export const AlbumForReviewingCountCheck = gql`
  query AlbumForReviewingCountCheck($id: RecordID!) {
    album(id: $id) {
      id
      photos_for_review: photos(
        include_unprocessed: true
        album_photo_filter: REVIEWING
      ) {
        total
      }
    }
  }
`;

export const AlbumForSpecialtyAlbumInvites = gql`
  query AlbumForSpecialtyAlbumInvites($id: RecordID!) {
    album(id: $id) {
      id
      name
      creator {
        id
      }
      album_type
      album_subtype
      enable_external_upload
      external_uploads_require_review
      is_video_album
      is_memory_book
      is_wedding_day_album
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      purchased_event_addon_types
      slideshow {
        status
      }
      root_album_folder {
        id
        name
        album_type
        album_subtype
        individual_albums
        account {
          id
          name
          organization_name
        }
        event_settings {
          ...AlbumFolderEventSettingsFields
        }
      }
    }
  }
  ${AlbumFolderEventSettingsFields}
  ${AutoContributorSettingsDetails}
`;

export const AlbumWithSelectedIdentitiesForIdentity = gql`
  query albumWithSelectedIdentitiesForIdentity(
    $id: RecordID!
    $identity_ids: [RecordID!]
    $dnpStatusFilter: DnpStatusFilterEnum = ALL
    $grade: GradeEnum
  ) {
    album(id: $id) {
      id
      selected_identities(
        slice: { limit: 9999 }
        identity_ids: $identity_ids
        dnp_status_filter: $dnpStatusFilter
        grade: $grade
      ) {
        total
        records {
          id
          from_invite
          created_at
          album_membership {
            id
            is_deleted
            album {
              id
              name
            }
          }
          identity {
            id
            name
            last_name
            relationship
            current_photo {
              ...PhotoThumbnails
            }
            current_matching_photo {
              ...PhotoThumbnails
            }
            roster_identity {
              id
              selfie_photo {
                ...PhotoThumbnails
              }
            }
            account {
              id
              name
              phone_number
              current_selfie {
                ...PhotoThumbnails
              }
            }
            ...AccountIdentityName
          }
          matched_photos_extended {
            total
          }
          purchased_photos {
            total
          }
          face_highlighted_photos {
            total
          }
          cabin_name
          jersey_number
          school_class
          has_purchased_album_identity
          matched_photo_notifications(slice: { limit: 1 }) {
            total
            records {
              created_at
            }
          }
          claimed
        }
      }
    }
  }
  ${PhotoThumbnails}
  ${AccountIdentityName}
`;

export const AlbumUserUploadedVideos = gql`
  query albumUserUploadedVideos($id: RecordID!, $accountId: RecordID!) {
    album(id: $id) {
      id
      user_uploaded_videos: videos(
        processed_only: false
        uploader_ids: [$accountId]
      ) {
        total
        records {
          id
          type
        }
      }
    }
  }
`;

export const AlbumForPendingAutoSpotlightGenerationCheck = gql`
  query AlbumForAutoSpotlightGenerationCheck($id: RecordID!) {
    album(id: $id) {
      id
      has_pending_auto_face_highlights_create
    }
  }
`;

export const AlbumForExpiration = gql`
  query AlbumForExpiration($id: RecordID!) {
    album(id: $id) {
      id
      root_album_folder {
        id
        name
        album_type
        expiration_status
        expiration_date
      }
    }
  }
`;

export const AlbumForGreenScreenCheck = gql`
  query AlbumForGreenScreenCheck($id: RecordID!) {
    album(id: $id) {
      id
      pricing_categories {
        total
        records {
          id
          pricing_category {
            id
            name
          }
        }
      }
      photos(include_unprocessed: true) {
        total
      }
      green_screen_enabled
      root_album_folder {
        id
        name
        green_screen_enabled
      }
    }
  }
`;

export const AlbumForTeamTagWarning = gql`
  query AlbumForTeamTagWarning(
    $id: RecordID!
    $album_folder_tag_ids: [RecordID]
  ) {
    album(id: $id) {
      id
      photos(
        include_unprocessed: true
        album_folder_tag_ids: $album_folder_tag_ids
      ) {
        total
      }
    }
  }
`;

export const AlbumForSlideshowCuration = gql`
  query albumForSlideshowCuration(
    $id: RecordID!
    $limit: Int
    $offset: Int = 25
    $include_album_photos: Boolean!
    $include_face_highlights: Boolean!
    $include_slideshow_photos: Boolean!
    $include_favorited_photos: Boolean!
    $favorited_by: FavoritedPhotosFilterEnum
  ) {
    album(id: $id) {
      id
      creator {
        id
      }
      slideshow {
        mode
      }
      members_with_matched_photos {
        total
      }
      photos_for_review: photos(
        include_unprocessed: true
        album_photo_filter: REVIEWING
      ) {
        total
      }
      unmatched_photos: photos(
        include_unprocessed: false
        ungrouped_only: true
        without_matched_photos_only: true
        slice: { limit: 1 }
        album_photo_filter: ALL
      ) {
        total
      }
      photos(
        include_unprocessed: false
        ungrouped_only: true
        slice: { limit: $limit, offset: $offset }
        album_photo_filter: PUBLISHED
      ) @include(if: $include_album_photos) {
        total
        records {
          ...SlideshowCurationPhotoDetails
        }
      }
      face_highlighted_photos(slice: { limit: $limit, offset: $offset })
        @include(if: $include_face_highlights) {
        total
        records {
          id
          photo {
            ...SlideshowCurationPhotoDetails
          }
        }
      }
      favorited_photos(
        slice: { limit: $limit, offset: $offset }
        favorited_by: $favorited_by
      ) @include(if: $include_favorited_photos) {
        total
        records {
          id
          photo {
            ...SlideshowCurationPhotoDetails
          }
        }
      }
      slideshow @include(if: $include_slideshow_photos) {
        ...AlbumSlideshowDetails
        photos(slice: { limit: $limit, offset: $offset }) {
          total
          records {
            photo {
              ...SlideshowCurationPhotoDetails
            }
            display_order
          }
        }
        excluded_photos(slice: { limit: 999 }) {
          total
          records {
            photo {
              id
            }
          }
        }
        selected_photos(slice: { limit: $limit, offset: $offset }) {
          total
          records {
            photo {
              ...SlideshowCurationPhotoDetails
            }
          }
        }
      }
    }
  }
  ${AlbumSlideshowDetails}
  ${SlideshowCurationPhotoDetails}
`;

export const AlbumForManualMatching = gql`
  query albumForManualMatching(
    $id: RecordID!
    $limit: Int
    $offset: Int = 25
    $include_album_photos: Boolean!
    $include_unmatched_photos: Boolean!
  ) {
    album(id: $id) {
      id
      name
      album_type
      album_subtype
      picture_day_enabled
      creator {
        id
      }
      slideshow {
        mode
      }
      members_with_matched_photos {
        total
      }
      root_album_folder {
        id
        name
        pricing_categories {
          total
          records {
            id
            pricing_category {
              id
              name
            }
          }
        }
      }
      photos(
        include_unprocessed: false
        slice: { limit: $limit, offset: $offset }
        album_photo_filter: ALL
      ) @include(if: $include_album_photos) {
        total
        records {
          ...ManualMatchingPhotoDetails
        }
      }
      photos_for_review: photos(
        include_unprocessed: true
        album_photo_filter: REVIEWING
      ) {
        total
      }
      unmatched_photos: photos(
        include_unprocessed: false
        without_matched_photos_only: true
        slice: { limit: $limit, offset: $offset }
        album_photo_filter: ALL
      ) @include(if: $include_unmatched_photos) {
        total
        records {
          ...ManualMatchingPhotoDetails
        }
      }
    }
  }
  ${ManualMatchingPhotoDetails}
`;

export const AlbumForHierarchyGrades = gql`
  query AlbumForHierarchyGrades($id: RecordID!) {
    album(id: $id) {
      id
      name
      ...AlbumHierarchyGradeDetails
    }
  }
  ${AlbumHierarchyGradeDetails}
`;

export const AlbumIdentitiesForIdentitySelector = gql`
  query AlbumIdentitiesForIdentitySelector(
    $id: RecordID!
    $has_yearbook_photo: Boolean
  ) {
    album(id: $id) {
      id
      picture_day_enabled
      root_album_folder {
        id
      }
      photos_for_review: photos(
        include_unprocessed: true
        album_photo_filter: REVIEWING
      ) {
        total
      }
      unmatched_photos: photos(
        include_unprocessed: false
        ungrouped_only: true
        without_matched_photos_only: true
        slice: { limit: 1 }
        album_photo_filter: ALL
      ) {
        total
      }
      selected_identities(
        slice: { limit: 9999 }
        unique_roster_identities: true
        has_matched_photos: true
        has_yearbook_photo: $has_yearbook_photo
      ) {
        total
        records {
          id
          identity {
            id
            name
            roster_identity {
              id
            }
            current_matching_photo {
              ...PhotoThumbnails
            }
          }
          matched_photos_extended {
            total
          }
        }
      }
    }
  }
  ${PhotoThumbnails}
`;

export const AlbumForYearbookExports = gql`
  query AlbumForYearbookExports($id: RecordID!) {
    album(id: $id) {
      id
      name
      album_type
      album_subtype
      root_album_folder {
        id
      }
      yearbook_exports(slice: { limit: 999 }) {
        total
        records {
          ...YearbookExportDetails
        }
      }
    }
  }
  ${YearbookExportDetails}
`;

// In this mutation the $id is an AlbumFolder ID. Reasoning behind this is the
// Yearbook fragment, which uses the $id parameter as an album_folder_id. If use
// case long term determines the need to change this, it will be done then.
export const AlbumIdentitiesForYearbookPhotos = gql`
  query AlbumIdentitiesForYearbookPhotos(
    $id: RecordID!
    $album_folder_id: RecordID!
    $has_yearbook_photo: Boolean
    $limit: Int = 100
    $offset: Int = 0
  ) {
    album(id: $id) {
      id
      name
      selected_identities(
        slice: { offset: $offset, limit: $limit }
        unique_roster_identities: false
        has_yearbook_photo: $has_yearbook_photo
      ) {
        total
        records {
          id
          identity {
            id
            name
            last_name
            ...IdentityHasRosterIdentityDetails
            matched_photos_extended(
              album_ids: [$id]
              slice: { limit: 1 }
              matched_photos_sort: BEST_SCORE
            ) {
              total
              records {
                id
                photo {
                  id
                }
              }
            }
            ...IdentityYearbookPhotoDetails
          }
        }
      }
    }
  }
  ${IdentityHasRosterIdentityDetails}
  ${IdentityYearbookPhotoDetails}
`;

export const AlbumForPhotoForeground = gql`
  query albumForPhotoForeground(
    $id: RecordID!
    $limit: Int
    $offset: Int
    $album_photo_filter: AlbumPhotoFilterEnum = PUBLISHED
    $uploader_ids: [RecordID]
    $sort_by_shoot_time: Boolean
    $original_filename: String
    $search_query: String
    $include_photos: Boolean!
  ) {
    album(id: $id) {
      id
      ...AlbumForegroundCountDetails
      album_folder {
        id
        individual_photos_upload_type
        team_photos_upload_type
      }
      root_album_folder {
        id
        individual_photos_upload_type
        team_photos_upload_type
      }
      photos(
        include_unprocessed: true
        ungrouped_only: true
        slice: { limit: $limit, offset: $offset }
        album_photo_filter: $album_photo_filter
        sort_by_shoot_time: $sort_by_shoot_time
        uploader_ids: $uploader_ids
        original_filename: $original_filename
        search_query: $search_query
      ) @include(if: $include_photos) {
        total
        count
        records {
          id
          ...PhotoWatermarkThumbnailDetails
          ...PhotoForegroundDetails
        }
      }
    }
  }
  ${AlbumForegroundCountDetails}
  ${PhotoWatermarkThumbnailDetails}
  ${PhotoForegroundDetails}
`;

export const AlbumForPhotoForegroundCounts = gql`
  query albumForPhotoForegroundCounts($id: RecordID!) {
    album(id: $id) {
      id
      ...AlbumForegroundCountDetails
    }
  }
  ${AlbumForegroundCountDetails}
`;
